import { useState } from 'react';

/**
 * SitePreview — embedded live-site preview wrapped in browser chrome.
 *
 * Shape is "Mac browser window": three dots, a URL pill, a visit button,
 * and an iframe underneath at a fixed aspect ratio. Replaces the animated
 * before/after slider on the case-study pages now that we have real work
 * to show — the preview itself is the proof.
 *
 * CAVEATS (these matter — read before deploying):
 *   1. MIXED CONTENT. If the site is served over http:// (Vienna is) and
 *      webnova is deployed over https://, browsers block the iframe. In
 *      dev over http://localhost this works fine; in prod it won't until
 *      the target site moves to https or we proxy it.
 *   2. X-FRAME-OPTIONS / CSP. Some sites set `X-Frame-Options: DENY` or
 *      `Content-Security-Policy: frame-ancestors` headers that block
 *      embedding from any origin. There's no reliable way to detect this
 *      from JS before the iframe tries to load — the tab stays blank and
 *      no `error` event fires.
 *   3. Cross-origin iframes are heavy. The site loads in a real browser
 *      context: fonts, analytics, cookies, the works. Lazy-load it.
 *
 * Mitigations implemented here:
 *   - `loading="lazy"` so the iframe doesn't fire until it's near the
 *     viewport.
 *   - `referrerPolicy="no-referrer"` so we don't leak where the embed
 *     came from to the target's analytics.
 *   - `sandbox` left permissive (allow-scripts, allow-same-origin, etc.)
 *     because the whole point is a faithful preview; a stricter sandbox
 *     breaks any site that does client-side rendering.
 *   - Optional `fallbackImg`: a screenshot shown underneath the iframe
 *     which becomes visible if the iframe background is transparent.
 *     Most useful when combined with a `showFallback` state the caller
 *     can flip after a reasonable timeout.
 */
interface SitePreviewProps {
  /** Full URL to embed, including protocol. */
  src: string;
  /** Human-readable display for the URL pill. Defaults to src minus protocol. */
  label?: string;
  /** CSS aspect ratio for the viewport under the chrome. */
  aspect?: string;
  /** Accessible title for the iframe / image. */
  title: string;
  /**
   * If provided, render a static screenshot instead of a live iframe.
   * The chrome stays the same so Chef (live iframe) and Vienna (static
   * image) read as the same component — only the contents differ. We do
   * this for sites that can't be embedded due to mixed-content (http://
   * Vienna inside an https WebNova) or X-Frame-Options blocks. The
   * "Visit" button still points at `src`, so the click-through path is
   * unchanged.
   */
  staticImg?: string;
  /** 2x variant of staticImg for hi-DPI displays. */
  staticImg2x?: string;
  /**
   * Optional fallback image (behind iframe) for when live embedding is
   * blocked at runtime. Distinct from `staticImg`: this is a defensive
   * layer; `staticImg` is the deliberate choice.
   */
  fallbackImg?: string;
}

export default function SitePreview({
  src,
  label,
  aspect = '16/10',
  title,
  staticImg,
  staticImg2x,
  fallbackImg,
}: SitePreviewProps) {
  const [loaded, setLoaded] = useState<boolean>(false);

  const display = label ?? src.replace(/^https?:\/\//, '');
  const isStatic = Boolean(staticImg);

  return (
    <div className="site-preview">
      <div className="site-preview-chrome">
        <div className="site-preview-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="site-preview-url" aria-hidden="true">
          {display}
        </div>
        <a
          className="site-preview-visit"
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${display} in a new tab`}
        >
          Visit
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </a>
      </div>

      <div className="site-preview-viewport" style={{ aspectRatio: aspect }}>
        {isStatic ? (
          // Static mode: the screenshot IS the content. We mark it as
          // an `<img>` (not aria-hidden) and use the title as alt text
          // so screen readers still describe it.
          <img
            className="site-preview-static"
            src={staticImg}
            srcSet={staticImg2x ? `${staticImg} 1x, ${staticImg2x} 2x` : undefined}
            alt={title}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        ) : (
          <>
            {fallbackImg ? (
              // Rendered beneath the iframe; peeks through if the embed
              // is blocked.
              <img
                className="site-preview-fallback"
                src={fallbackImg}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
              />
            ) : null}
            <iframe
              className={`site-preview-frame ${loaded ? 'loaded' : ''}`}
              src={src}
              title={title}
              loading="lazy"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              onLoad={() => setLoaded(true)}
            />
          </>
        )}
      </div>
    </div>
  );
}
